// Extrait de ClinicAdminDashboard.js (handover bloc F3-B) — présentationnel : filtres, tri, handlers et gardes restent dans la page.
import {
  Search, Users, Archive, Trash2, Plus, Clock, UserCheck, FileText,
  RefreshCw, Key, Edit2, ArrowUpDown, ArrowUp, ArrowDown
} from "lucide-react";
import { OpenAlertBadge, StatusBadge } from "@/components/shared/StatusBadge";

export default function ClinicPatientsTab({
  filteredPatients,
  activePatientCount,
  inactivePatientCount,
  patientFilter,
  onPatientFilterChange,
  showInactivePatients,
  onShowInactiveChange,
  sortBy,
  sortDir,
  onToggleSort,
  onOpenResetClinic,
  onOpenCreate,
  onRestore,
  onResetAccessCode,
  onEdit,
  onArchive,
  onDeleteRequest,
}) {
  const SortHeader = ({ column, children }) => {
    const active = sortBy === column;
    return (
      <th
        className="text-left p-4 text-sm font-medium text-slate-600 cursor-pointer select-none hover:text-slate-900 transition-colors whitespace-nowrap"
        onClick={() => onToggleSort(column)}
      >
        <div className="flex items-center gap-1.5">
          {children}
          {active ? (sortDir === "asc" ? <ArrowUp className="w-3.5 h-3.5 text-emerald-600" /> : <ArrowDown className="w-3.5 h-3.5 text-emerald-600" />) : <ArrowUpDown className="w-3.5 h-3.5 text-slate-300" />}
        </div>
      </th>
    );
  };

  return (
    <div className="space-y-4">
      {/* Actions & Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={patientFilter.search}
              onChange={e => onPatientFilterChange(f => ({ ...f, search: e.target.value }))}
              data-testid="admin-patient-search-input"
              className="pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm w-64"
            />
          </div>
          <select
            value={patientFilter.status}
            onChange={e => onPatientFilterChange(f => ({ ...f, status: e.target.value }))}
            data-testid="admin-patient-status-filter"
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          >
            <option value="">Tous les statuts</option>
            <option value="STABLE">Stable</option>
            <option value="TO_MONITOR">À surveiller</option>
            <option value="CRITICAL">Critique</option>
          </select>
        </div>
        <div className="flex items-center gap-3">
          {/* Segmented view switcher: Actifs / Archivés */}
          <div className="inline-flex bg-slate-100 rounded-lg p-1" data-testid="patients-view-switcher">
            <button
              onClick={() => onShowInactiveChange(false)}
              data-testid="view-active-patients"
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                !showInactivePatients
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Users className="w-4 h-4" />
              Actifs ({activePatientCount})
            </button>
            <button
              onClick={() => onShowInactiveChange(true)}
              data-testid="view-archived-patients"
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                showInactivePatients
                  ? "bg-white text-amber-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Archive className="w-4 h-4" />
              Archives ({inactivePatientCount})
            </button>
          </div>
          <button
            onClick={onOpenResetClinic}
            data-testid="reset-clinic-btn"
            className="flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 px-3 py-2 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors"
            title="Supprimer tous les patients de la clinique (utile avant demarrage)"
          >
            <Trash2 className="w-4 h-4" />
            Reset donnees de test
          </button>
          <button
            onClick={onOpenCreate}
            data-testid="add-patient-btn"
            className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouveau patient
          </button>
        </div>
      </div>

      {/* Patients Table */}
      <div className="dash-card overflow-x-auto p-0">
        <table className="w-full min-w-[900px]">
          <thead className="bg-slate-50">
            <tr>
              <SortHeader column="name">Patient</SortHeader>
              <th className="text-left p-4 text-sm font-medium text-slate-600 whitespace-nowrap">Téléphone</th>
              <th className="text-left p-4 text-sm font-medium text-slate-600 whitespace-nowrap">Cancer</th>
              <th className="text-left p-4 text-sm font-medium text-slate-600 whitespace-nowrap">Traitement</th>
              <SortHeader column="lastSeen">Dernière visite</SortHeader>
              <th className="text-left p-4 text-sm font-medium text-slate-600 whitespace-nowrap">Dossier</th>
              <SortHeader column="status">Statut actuel</SortHeader>
              <th className="text-left p-4 text-sm font-medium text-slate-600 whitespace-nowrap">Alertes ouvertes</th>
              <th className="text-right p-4 text-sm font-medium text-slate-600 whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredPatients.map(patient => {
              const patientDoctors = (patient.doctors || []).length > 0 
                ? patient.doctors 
                : (patient.doctorId ? [{ id: patient.doctorId, name: patient.doctorName }] : []);
              const patientNurses = (patient.nurses || []).length > 0
                ? patient.nurses
                : (patient.nurseId ? [{ id: patient.nurseId, name: patient.nurseName }] : []);
              // Display new fields or fallback to legacy
              const hasNewFields = patient.cancerType || patient.treatmentType;
              const cancerDisplay = hasNewFields 
                ? (patient.cancerTypeDetail ? `${patient.cancerType} (${patient.cancerTypeDetail})` : patient.cancerType)
                : patient.pathology;
              const treatmentDisplay = hasNewFields ? patient.treatmentType : patient.treatment;
              return (
                <tr key={patient.id} data-testid={`patient-row-${patient.id}`} className="hover:bg-slate-50">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-semibold">
                        {patient.firstName?.[0]}{patient.lastName?.[0]}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{patient.firstName} {patient.lastName}</p>
                        <p className="text-xs text-slate-400">
                          {patient.startDate ? `Depuis ${new Date(patient.startDate).toLocaleDateString("fr-FR")}` : ""}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-slate-600">{patient.phone}</td>
                  <td className="p-4 text-sm text-slate-600">{cancerDisplay || "—"}</td>
                  <td className="p-4 text-sm text-slate-600">{treatmentDisplay || "—"}</td>
                  <td className="p-4" data-testid={`patient-last-seen-${patient.id}`}>
                    {patient.lastSeenAt ? (
                      <div className="flex items-center gap-1.5 text-sm text-slate-600">
                        <Clock className="w-3.5 h-3.5 text-slate-400" />
                        <span>{new Date(patient.lastSeenAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</span>
                        <span className="text-xs text-slate-400">{new Date(patient.lastSeenAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400 italic">Aucune visite</span>
                    )}
                  </td>
                  <td className="p-4">
                    {patient.isActive === false ? (
                      <span className="text-xs px-2 py-1 rounded-full font-medium bg-slate-200 text-slate-600 flex items-center gap-1 w-fit">
                        <Archive className="w-3 h-3" />
                        Archivé
                      </span>
                    ) : patient.clinicalEvaluationComplete ? (
                      <span className="text-xs px-2 py-1 rounded-full font-medium bg-emerald-100 text-emerald-700 flex items-center gap-1 w-fit">
                        <UserCheck className="w-3 h-3" />
                        Actif
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded-full font-medium bg-amber-100 text-amber-700 flex items-center gap-1 w-fit">
                        <FileText className="w-3 h-3" />
                        Éval. requise
                      </span>
                    )}
                  </td>
                  <td className="p-4" data-testid={`admin-patient-current-status-${patient.id}`}>
                    <StatusBadge status={patient.currentStatus || patient.status || "STABLE"} testId={`admin-patient-current-status-badge-${patient.id}`} />
                  </td>
                  <td className="p-4" data-testid={`admin-patient-open-alerts-${patient.id}`}>
                    <OpenAlertBadge count={patient.openAlertsCount} level={patient.highestOpenAlertLevel} testId={`admin-patient-open-alerts-badge-${patient.id}`} />
                    {!patient.openAlertsCount && <span className="text-xs text-slate-400">Aucune</span>}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      {patient.isActive === false ? (
                        <button
                          onClick={() => onRestore(patient.id)}
                          data-testid={`restore-patient-${patient.id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                          title="Restaurer"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                          Restaurer
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => onResetAccessCode(patient)}
                            data-testid={`reset-patient-access-code-${patient.id}`}
                            className="p-2 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition-colors"
                            title="Générer un code d’accès"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onEdit(patient)}
                            data-testid={`edit-patient-${patient.id}`}
                            className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                            title="Modifier"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onArchive(patient.id)}
                            data-testid={`archive-patient-${patient.id}`}
                            className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            title="Archiver"
                          >
                            <Archive className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onDeleteRequest(patient)}
                            data-testid={`delete-patient-${patient.id}`}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Supprimer définitivement"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredPatients.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>Aucun patient trouvé</p>
          </div>
        )}
      </div>
    </div>
  );
}
